/* ============================================================================
   Productos. Qué renglón de la canasta empujó el cambio.

   · Barras divergentes alrededor de cero: naranja encareció, azul abarató.
     La flecha y el signo son el canal redundante, así que el color nunca carga
     solo el sentido.
   · Los filtros van en una sola fila arriba y alcanzan a todo lo de abajo.
   · El horizonte de comparación se limita a lo que los datos soportan. La serie
     por producto arranca en julio de 2025: hoy no existe comparación contra el
     mismo mes del año anterior, y ofrecerla producía «0.0 % sin cambio» en los
     42 renglones. Un vacío se dice, no se rellena con un cero.
   · Cada renglón abre una hoja con su serie mensual completa.
   ========================================================================== */

import { useEffect, useMemo, useState } from 'react'
import { BarList, type BarRow } from '../charts/BarList'
import { LineChart } from '../charts/LineChart'
import { Segmented } from '../components/Segmented'
import { Sheet } from '../components/Sheet'
import { Callout, Card, FilterBar, FilterField, Section, Select } from '../components/ui'
import { Delta } from '../components/Delta'
import { AMBITO_COLOR, AMBITO_LABEL, AMBITO_OPTIONS, dataset, type Ambito } from '../data/dataset'
import {
  PRODUCT_SORTS,
  availableHorizons,
  changeFor,
  productChanges,
  productSeriesIndex,
  sortProductChanges,
  type Horizon,
  type ProductSort,
} from '../data/derive'
import { CATEGORY_LABEL, type ProductCategory } from '../data/schema'
import {
  currency,
  formatPeriodTitle,
  percentDelta,
  pluralize,
  shortenLabel,
} from '../lib/format'

type CategoryFilter = 'todas' | ProductCategory

export function Productos({
  ambito,
  onAmbitoChange,
}: {
  ambito: Ambito
  onAmbitoChange: (value: Ambito) => void
}) {
  const [sort, setSort] = useState<ProductSort>('sube')
  const [category, setCategory] = useState<CategoryFilter>('todas')
  const [selected, setSelected] = useState<string | null>(null)

  const changes = useMemo(() => productChanges(dataset, ambito, 'precio'), [ambito])
  const valueIndex = useMemo(() => productSeriesIndex(dataset, ambito, 'valor-mensual'), [ambito])

  const horizons = useMemo(() => availableHorizons(changes), [changes])
  const [horizon, setHorizon] = useState<Horizon>(() => horizons[horizons.length - 1] ?? 'monthly')

  // Si cambia el ámbito y el horizonte activo deja de existir, se reencuadra.
  useEffect(() => {
    if (!horizons.includes(horizon)) setHorizon(horizons[horizons.length - 1] ?? 'monthly')
  }, [horizons, horizon])

  /* El periodo del desglose por producto viene de los boletines PDF y el de la
     serie principal del Banco de Indicadores: son dos fuentes y pueden terminar
     en meses distintos. El rótulo tiene que salir del dato que se muestra. */
  const productPeriod =
    changes.reduce<string | null>(
      (acc, c) => (acc === null || c.latest.period > acc ? c.latest.period : acc),
      null,
    ) ?? dataset.latestPeriod

  const spanMonths = changes.find((c) => c.sinceStart !== null)?.sinceStart?.months ?? 0
  const firstPeriod = changes[0]?.series.points[0]?.period

  const horizonOptions = useMemo(
    () =>
      horizons.map((value) => ({
        value,
        label:
          value === 'monthly'
            ? 'Mes'
            : value === 'annual'
              ? 'Año'
              : `${spanMonths} meses`,
        hint:
          value === 'monthly'
            ? 'Contra el mes anterior'
            : value === 'annual'
              ? 'Contra el mismo mes del año anterior'
              : `Acumulado desde ${firstPeriod ? formatPeriodTitle(firstPeriod).toLowerCase() : 'el inicio de la serie'}`,
      })),
    [horizons, spanMonths, firstPeriod],
  )

  const categoryOptions = useMemo(() => {
    const present = new Set(changes.map((c) => c.product.category))
    const options: { value: CategoryFilter; label: string }[] = [
      { value: 'todas', label: 'Todas las categorías' },
    ]
    for (const [key, label] of Object.entries(CATEGORY_LABEL) as [ProductCategory, string][]) {
      if (present.has(key)) options.push({ value: key, label })
    }
    return options
  }, [changes])

  const filtered = useMemo(() => {
    const base =
      category === 'todas' ? changes : changes.filter((c) => c.product.category === category)
    return sortProductChanges(base, sort, horizon)
  }, [changes, category, sort, horizon])

  const usesPrice = sort === 'price-desc'

  const comparable = useMemo(
    () => filtered.filter((c) => !c.product.unit.includes('ración')),
    [filtered],
  )

  const comparableCount = useMemo(
    () => changes.filter((c) => !c.product.unit.includes('ración')).length,
    [changes],
  )

  const rows = useMemo<BarRow[]>(
    () =>
      comparable.map((c) => ({
        id: c.product.id,
        label: c.product.name,
        sublabel: `por ${c.product.unit} · ${CATEGORY_LABEL[c.product.category]}`,
        /* Las dos columnas tienen que decir cosas DISTINTAS. Ordenando por precio,
           antes el precio iba en las dos y el renglón se leía «$49.32 · $49.32»
           bajo dos encabezados titulados los dos «Precio». Aquí se intercambian:
           la barra mide el precio —que es lo que ordena— y la columna de valor
           lleva su cambio, que es la otra mitad de la historia. */
        value: usesPrice ? (changeFor(c, horizon)?.percent ?? Number.NaN) : c.latest.value,
        magnitude: usesPrice ? c.latest.value : (changeFor(c, horizon)?.percent ?? null),
      })),
    [comparable, usesPrice, horizon],
  )

  const rations = useMemo(
    () => filtered.filter((c) => c.product.unit.includes('ración')),
    [filtered],
  )

  const selectedChange = selected ? changes.find((c) => c.product.id === selected) : undefined

  /* La cantidad de referencia es POR ÁMBITO: la canasta rural y la urbana
     asignan gramos distintos al mismo renglón. Si falta la del ámbito activo se
     omite la frase, en vez de mostrar la del otro. */
  const gramosDelAmbito = selectedChange?.product.gramsPerPersonMonth?.[ambito]

  /**
   * Decimales con los que imprimir el precio de la serie abierta: los suficientes
   * para que dos meses contiguos no se lean iguales.
   */
  const productDecimals = useMemo(() => {
    if (!selectedChange) return 2
    const values = selectedChange.series.points.map((p) => p.value)
    if (values.length === 0) return 2
    const span = Math.max(...values) - Math.min(...values)
    if (span >= 20) return 0
    if (span >= 2) return 1
    return 2
  }, [selectedChange])

  /* Sobre `filtered`, no sobre `changes`: si el lector acotó a una categoría,
     una tarjeta que anuncia el máximo de TODA la canasta contradice la tabla que
     tiene debajo. */
  const withChange = useMemo(
    () => filtered.filter((c) => changeFor(c, horizon) !== null && !c.product.unit.includes('ración')),
    [filtered, horizon],
  )

  const biggest = useMemo(() => {
    if (withChange.length === 0) return null
    return withChange.reduce((a, b) =>
      changeFor(a, horizon)!.percent >= changeFor(b, horizon)!.percent ? a : b,
    )
  }, [withChange, horizon])

  const cheapest = useMemo(() => {
    if (withChange.length === 0) return null
    return withChange.reduce((a, b) =>
      changeFor(a, horizon)!.percent <= changeFor(b, horizon)!.percent ? a : b,
    )
  }, [withChange, horizon])

  /* Dos formas porque el género tiene que concordar: «variación acumulada»
     pero «cambio acumulado». */
  const horizonWord =
    horizon === 'monthly'
      ? 'mensual'
      : horizon === 'annual'
        ? 'anual'
        : `acumulada en ${spanMonths} meses`

  const horizonWordM =
    horizon === 'monthly'
      ? 'mensual'
      : horizon === 'annual'
        ? 'anual'
        : `acumulado en ${spanMonths} meses`

  const horizonAgainst =
    horizon === 'monthly'
      ? 'el mes anterior'
      : horizon === 'annual'
        ? 'el mismo mes del año anterior'
        : firstPeriod
          ? formatPeriodTitle(firstPeriod).toLowerCase()
          : 'el inicio de la serie'

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Productos</p>
        <h1 className="t-title1">Qué se encareció dentro de la canasta</h1>
        <p className="t-body ink-2">
          El INEGI publica cada mes el precio por kilogramo o litro de cada renglón de la canasta
          alimentaria, en el ámbito rural y en el urbano. Aquí están ordenados por su variación, con
          la serie completa de cada uno. De los {changes.length} renglones, {comparableCount} tienen
          precio por peso o volumen; los otros {changes.length - comparableCount} se publican por
          ración y se detallan aparte.
        </p>
      </header>

      <FilterBar ariaLabel="Filtros de productos">
        <FilterField label="Ámbito">
          <Segmented
            options={AMBITO_OPTIONS}
            value={ambito}
            onChange={onAmbitoChange}
            ariaLabel="Ámbito geográfico" />
        </FilterField>
        <FilterField label="Comparar contra">
          <Segmented
            options={horizonOptions}
            value={horizon}
            onChange={setHorizon}
            ariaLabel="Horizonte de comparación" />
        </FilterField>
        <FilterField label="Ordenar por">
          <Select value={sort} onChange={setSort} options={PRODUCT_SORTS} ariaLabel="Ordenar por" />
        </FilterField>
        <FilterField label="Categoría">
          <Select
            value={category}
            onChange={setCategory}
            options={categoryOptions}
            ariaLabel="Categoría" />
        </FilterField>
      </FilterBar>

      {biggest && cheapest ? (
        <div className="grid grid--2">
          <Card>
            <p className="t-micro ink-3">Mayor alza {horizonWord}</p>
            <p className="t-title2 highlight-name">{biggest.product.name}</p>
            <div className="row">
              <Delta value={changeFor(biggest, horizon)!.percent} size="md" />
              <span className="t-footnote ink-2">
                {currency(changeFor(biggest, horizon)!.from.value)} →{' '}
                {currency(changeFor(biggest, horizon)!.to.value)} por {biggest.product.unit}
              </span>
            </div>
          </Card>
          <Card>
            <p className="t-micro ink-3">Mayor baja {horizonWord}</p>
            <p className="t-title2 highlight-name">{cheapest.product.name}</p>
            <div className="row">
              <Delta value={changeFor(cheapest, horizon)!.percent} size="md" />
              <span className="t-footnote ink-2">
                {currency(changeFor(cheapest, horizon)!.from.value)} →{' '}
                {currency(changeFor(cheapest, horizon)!.to.value)} por {cheapest.product.unit}
              </span>
            </div>
          </Card>
        </div>
      ) : null}

      <Section
        title={
          usesPrice
            ? `Precio por unidad, ámbito ${AMBITO_LABEL[ambito]}`
            : `Variación ${horizonWord} del precio, ámbito ${AMBITO_LABEL[ambito]}`
        }
        description={
          usesPrice
            ? 'Ordenado por precio. La barra mide el precio unitario, no su cambio.'
            : `Barra a la derecha y naranja: se encareció. A la izquierda y azul: se abarató. Cero al centro. Comparación contra ${horizonAgainst}.`
        }
      >
        <Card>
          <BarList
            rows={rows}
            title={`${rows.length} ${pluralize(rows.length, 'renglón comparable', 'renglones comparables')}`}
            subtitle={`Precio por unidad en ${formatPeriodTitle(productPeriod).toLowerCase()}. Toque un renglón para ver su serie.${
              productPeriod === dataset.latestPeriod
                ? ''
                : ` El valor de la canasta ya está publicado a ${formatPeriodTitle(dataset.latestPeriod).toLowerCase()}, pero el desglose por producto todavía no.`
            }`}
            valueHeading={usesPrice ? `Cambio ${horizonWordM}` : 'Precio'}
            magnitudeHeading={usesPrice ? 'Precio' : `Cambio ${horizonWordM}`}
            /* Las dos columnas intercambian unidad al ordenar por precio, así que
               cada una necesita su propio formateador: con uno solo, el precio se
               imprimía como «+202.5 %». */
            formatValue={(value) =>
              Number.isNaN(value) ? 'sin dato' : usesPrice ? percentDelta(value) : currency(value)
            }
            formatMagnitude={(value) => currency(value)}
            mode={usesPrice ? 'magnitude' : 'diverging'}
            magnitudeKind={usesPrice ? 'currency' : 'percent'}
            valueKind={usesPrice ? 'percent' : 'currency'}
            onRowClick={(id) => setSelected(id)}
            footnote="Fuente: INEGI, cuadros de contenido y valor monetario del boletín mensual de Líneas de Pobreza."
          />
        </Card>
      </Section>

      {horizons.includes('annual') ? null : (
        <Callout kind="info" title="Todavía no hay comparación anual por producto">
          <p>
            El desglose por producto empieza en{' '}
            {firstPeriod ? formatPeriodTitle(firstPeriod).toLowerCase() : 'el primer boletín'}, el
            primer boletín mensual que el INEGI publicó tras asumir la medición de pobreza. Habrá
            comparación contra el mismo mes del año anterior cuando la serie cumpla trece meses; por
            ahora el horizonte más largo es el acumulado de {spanMonths} meses.
          </p>
          <p>
            La serie del valor total de la canasta sí llega hasta enero de 1992, y ahí sí hay
            comparación anual: está en Panorama.
          </p>
        </Callout>
      )}

      {rations.length > 0 ? (
        <Callout
          kind="warning"
          title={
            rations.length === 1
              ? 'Un renglón no se puede comparar por precio'
              : `${rations.length} renglones no se pueden comparar por precio`
          }
        >
          <p>
            {rations.map((r) => r.product.name).join(' y ')} se publican con precio por ración, no por
            kilogramo, así que quedan fuera de la tabla de arriba para no mezclar unidades. En el
            ámbito {AMBITO_LABEL[ambito]} pesan{' '}
            {rations
              .map((r) => {
                const point = valueIndex.get(r.product.id)?.points.at(-1)
                /* «Por persona» no es adorno: la serie es MXN_persona_mes y era la
                   única cifra de la app que se imprimía sin su unidad. Leída como
                   gasto del hogar, un renglón de la canasta parece cuatro veces
                   más barato de lo que la fuente dice. */
                return point
                  ? `${currency(point.value)} por persona al mes`
                  : 'un monto no disponible'
              })
              .join(' y ')}{' '}
            dentro de la canasta, y sí están incluidos en el total y en la despensa.
          </p>
        </Callout>
      ) : null}

      <Sheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selectedChange?.product.name ?? ''}
        subtitle={
          selectedChange
            ? `${CATEGORY_LABEL[selectedChange.product.category]} · precio por ${selectedChange.product.unit} · ámbito ${AMBITO_LABEL[ambito]}`
            : undefined
        }
      >
        {selectedChange ? (
          <>
            <div className="grid grid--kpi">
              <div className="stack stack--tight">
                <p className="t-micro ink-3">Precio actual</p>
                <p className="t-title1">{currency(selectedChange.latest.value)}</p>
                <p className="t-caption ink-3">por {selectedChange.product.unit}</p>
              </div>
              <div className="stack stack--tight">
                <p className="t-micro ink-3">Cambio mensual</p>
                {selectedChange.monthly ? (
                  <Delta value={selectedChange.monthly.percent} size="md" />
                ) : (
                  <p className="t-footnote ink-3">Sin dato</p>
                )}
              </div>
              <div className="stack stack--tight">
                <p className="t-micro ink-3">
                  {selectedChange.annual ? 'Cambio anual' : `Acumulado ${spanMonths} meses`}
                </p>
                {selectedChange.annual ?? selectedChange.sinceStart ? (
                  <Delta
                    value={(selectedChange.annual ?? selectedChange.sinceStart)!.percent}
                    size="md"
                  />
                ) : (
                  <p className="t-footnote ink-3">Sin dato</p>
                )}
              </div>
            </div>

            <LineChart
              series={[
                {
                  id: selectedChange.product.id,
                  label: selectedChange.product.name,
                  /* `shortLabel` es lo único que se imprime dentro del SVG, al
                     final de la curva, con un margen fijo. Nombres como «Agua
                     embotellada» o «Refrescos envasados» se cortaban ahí a media
                     palabra; el nombre completo sigue en `label`, que es lo que
                     leen la leyenda, el tooltip y la tabla espejo. */
                  shortLabel: shortenLabel(selectedChange.product.name),
                  color: AMBITO_COLOR[ambito],
                  points: selectedChange.series.points,
                },
              ]}
              title="Precio mensual publicado"
              subtitle={`Pesos por ${selectedChange.product.unit}, ámbito ${AMBITO_LABEL[ambito]}.`}
              /* Los decimales se derivan del RECORRIDO de la serie, no de un
                 literal. Con cero decimales fijos, «Agua embotellada» —que va de
                 $2.29 a $2.44— imprimía «$2» en los doce meses, en el tooltip y
                 en la tabla: la precisión quedaba por debajo de la señal que la
                 propia línea dibuja. */
              formatValue={(value) => currency(value, productDecimals)}
              unitLabel={`pesos por ${selectedChange.product.unit}`}
              height={240}
              area
            />

            {valueIndex.get(selectedChange.product.id) ? (
              <div className="stack stack--tight">
                <p className="t-micro ink-3">Peso dentro de la canasta</p>
                <p className="t-body">
                  Este renglón aporta{' '}
                  <strong>
                    {currency(valueIndex.get(selectedChange.product.id)!.points.at(-1)!.value)}
                  </strong>{' '}
                  al mes por persona
                  {gramosDelAmbito !== undefined
                    ? `, con un consumo de referencia de ${gramosDelAmbito.toLocaleString('es-MX')} gramos al mes`
                    : ''}
                  .
                </p>
              </div>
            ) : null}

            <p className="t-caption ink-3">
              La serie por producto arranca en{' '}
              {formatPeriodTitle(selectedChange.series.points[0]!.period).toLowerCase()}: es el primer
              boletín mensual que publicó el INEGI con el desglose completo.
            </p>
          </>
        ) : null}
      </Sheet>
    </>
  )
}
